// Results of games played in the browser (vs computer, daily challenge) go through a small
// persistent queue: an upload is only dropped once the server has accepted or definitively
// rejected it, so a flaky connection or a deploy never loses a game. Each upload remembers the
// player it was played as; it's sent with that player's current session, and waits (rather
// than being dropped) while that player is signed out.
//
// Every upload has its own storage key, so tabs never overwrite each other's queue, and is
// also kept in memory, so it still goes out if storage is full or unavailable. Two tabs may
// send the same upload; the server treats repeats as the same game.
import type { Me } from '@chessbut/shared';
import { ApiError, api } from '../lib/api.ts';
import { getSession, onPlayerSwitch, onTokenChange, setMe } from '../lib/session.ts';
import { keysStartingWith, load, save, trySave } from '../lib/storage.ts';

interface Upload {
  id: string;
  path: '/api/bot-games' | '/api/challenge';
  body: Record<string, unknown>;
  playerId: string | null;
  /** Session at the time; used if that player isn't the one signed in now. */
  token: string | null;
  at: number;
}

const KEY = 'upload:';
const MAX_AGE_MS = 7 * 24 * 3600_000;
const RETRY_MS = 60_000;

let flushing: Promise<void> | null = null;
let again = false;
let timer: ReturnType<typeof setTimeout> | null = null;
const waiters = new Map<string, { resolve: (r: unknown) => void; reject: (e: Error) => void }>();
/** Uploads the server refused as signed out; tried again when the session changes. */
const parked = new Set<string>();
/** This tab's uploads, in case storage doesn't keep them. */
const memory = new Map<string, Upload>();

function pending(): Upload[] {
  const byId = new Map(memory);
  for (const key of keysStartingWith(KEY)) {
    const u = load<Upload | null>(key, null);
    if (!u?.id) continue;
    if (Date.now() - u.at > MAX_AGE_MS) save(key, null);
    else byId.set(u.id, u);
  }
  return [...byId.values()].sort((a, b) => a.at - b.at);
}

function remove(id: string) {
  memory.delete(id);
  save(KEY + id, null);
}

export function newRef(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/** Network trouble, rate limits and server errors are worth retrying; anything else isn't. */
export function retryable(err: unknown): boolean {
  return !(err instanceof ApiError) || err.status === 0 || err.status === 429 || err.status >= 500;
}

function tokenFor(u: Upload): string | null {
  const s = getSession();
  return u.playerId && s.me?.id === u.playerId && s.token ? s.token : u.token;
}

/**
 * A result played before this browser had a session (first visit, slow start-up) belongs to
 * the first session that sends it. That owner is saved before sending and kept for every
 * retry, so a reply lost in transit can't lead to it being credited to someone else later.
 */
function claim(u: Upload): Upload {
  if (u.playerId || u.token) return u;
  const s = getSession();
  if (!s.me || !s.token) return u;
  const stored = load<Upload | null>(KEY + u.id, null);
  if (stored?.playerId || stored?.token) return stored; // another tab got there first
  const owned: Upload = { ...u, playerId: s.me.id, token: s.token };
  memory.set(u.id, owned);
  if (trySave(KEY + u.id, owned)) {
    const check = load<Upload | null>(KEY + u.id, null);
    if (check && check.playerId !== owned.playerId) return check;
  }
  return owned;
}

/**
 * Queues a result and tries to send it now. Resolves with the server's answer when it gets
 * through on this attempt; rejects with the error otherwise (it stays queued if retryable).
 */
export function submitResult<T extends { me?: Me }>(path: Upload['path'], body: Record<string, unknown>): Promise<T> {
  const s = getSession();
  const upload: Upload = { id: newRef(), path, body, playerId: s.me?.id ?? null, token: s.token, at: Date.now() };
  memory.set(upload.id, upload);
  if (!trySave(KEY + upload.id, upload)) console.warn('Could not save the result on this device; sending it now.');
  const result = new Promise<T>((resolve, reject) => waiters.set(upload.id, { resolve: resolve as (r: unknown) => void, reject }));
  void flushUploads();
  return result;
}

function settle(id: string, res: unknown, err: Error | null) {
  const w = waiters.get(id);
  waiters.delete(id);
  if (err) w?.reject(err);
  else w?.resolve(res);
}

/** 'retry' = offline or the server is struggling: stop and try everything again later. */
async function send(upload: Upload): Promise<'done' | 'parked' | 'retry'> {
  const u = claim(upload);
  const token = tokenFor(u);
  try {
    const res = await api<{ me?: Me }>(u.path, { body: u.body, token });
    remove(u.id);
    if (res.me && res.me.id === getSession().me?.id) setMe(res.me);
    settle(u.id, res, null);
    return 'done';
  } catch (err) {
    settle(u.id, null, err as Error);
    if (err instanceof ApiError && err.status === 401) {
      // Signed (back) in while this was in flight, or its owner changed: the next pass sends the
      // latest version of it with the right session.
      const latest = memory.get(u.id) ?? load<Upload | null>(KEY + u.id, null) ?? u;
      if (tokenFor(latest) !== token) {
        again = true;
        return 'parked';
      }
      parked.add(u.id);
      return 'parked';
    }
    if (retryable(err)) return 'retry';
    remove(u.id);
    return 'done';
  }
}

/** Sends everything sendable; true if it stopped early because of a retryable failure. */
async function drain(): Promise<boolean> {
  const tried = new Set<string>();
  for (;;) {
    const u = pending().find((x) => !tried.has(x.id) && !parked.has(x.id));
    if (!u) return false;
    tried.add(u.id);
    if ((await send(u)) === 'retry') {
      const err = new ApiError('Network error. Check your connection.', 0);
      for (const id of [...waiters.keys()]) settle(id, null, err);
      return true;
    }
  }
}

/** Sends everything still queued (on start-up, when the browser comes back online, after a failure). */
export function flushUploads(): Promise<void> {
  if (flushing) {
    again = true;
    return flushing;
  }
  again = false;
  // `.then` always runs after this assignment, even when there's nothing to send.
  const run = drain().then((failed): Promise<void> | void => {
    flushing = null;
    if (timer) clearTimeout(timer);
    timer = null;
    if (failed) timer = setTimeout(() => void flushUploads(), RETRY_MS);
    else if (again) return flushUploads();
  });
  flushing = run;
  return run;
}

export function startUploads() {
  // Earlier versions kept the whole queue under one key. Anything that can't be copied yet
  // stays there for next time.
  const legacy = load<Upload[] | null>('uploads', null);
  if (Array.isArray(legacy)) {
    const left = legacy.filter((u) => {
      if (!u?.id) return false;
      const upload = { ...u, playerId: u.playerId ?? null };
      memory.set(u.id, upload);
      return !trySave(KEY + u.id, upload);
    });
    if (left.length) trySave('uploads', left);
    else save('uploads', null);
  }
  // The session may have arrived since anything was parked as signed out.
  parked.clear();
  // A guest that signs in is folded into the account on the server, so its results go there.
  onPlayerSwitch((from, to) => {
    const s = getSession();
    for (const u of pending()) {
      if (u.playerId !== from) continue;
      const moved = { ...u, playerId: to, token: s.token };
      memory.set(u.id, moved);
      trySave(KEY + u.id, moved);
      parked.delete(u.id);
    }
    void flushUploads();
  });
  window.addEventListener('online', () => void flushUploads());
  onTokenChange(() => {
    parked.clear();
    void flushUploads();
  });
  void flushUploads();
}
